import { useState, useEffect, useMemo } from 'react';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import LeagueInfo from './LeagueInfo';
import WeeklyCalendar from './WeeklyCalendar';
import EncounterResultsSection from './EncounterResultsSection';
import LeagueRankingTable from './LeagueRankingTable';
import { Button } from '@/components/ui/button';
import { usePrizes } from '@/hooks/usePrizes';
import { CategoryPrizes } from '../PrizesDialog';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { useLeagueAttendance } from '@/hooks/useLeagueAttendance';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useLeagueSchedule, calculateGameDateStatus, extractDateFromFormatted, EliminationPhaseSchedule } from '@/hooks/useLeagueSchedule';
import { EliminationScheduleConfig } from './WeeklyCalendar';
import { useRoundRobinParticipants } from '@/hooks/useRoundRobinParticipants';
import { generateLeagueWeeks, calculateWeeksRemaining, formatTimeRange, formatTo12h, calculateWeekEndDate, interleaveRestWeeks } from '@/utils/leagueCalendarUtils';
import { parseLocalDate } from '@/utils/dateUtils';
import { addDays } from 'date-fns';
interface League {
  id: string;
  name: string;
  date: string;
  endDate?: string;
  club: string;
  modality: 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos';
  gameType: 'singles' | 'doubles';
  sport: 'padel' | 'tennis' | 'pickleball';
  category: 'masculina' | 'femenina' | 'mixtos';
  subcategory: string;
  partner?: string;
  currentStage: string;
  status: 'upcoming' | 'active' | 'finished';
  start_time?: string;
  end_time?: string;
  game_days?: string[];
  total_weeks?: number;
  matches_per_week?: number;
  organizer?: string;
  group_rules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additional_rules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
  };
  // Elimination phase fields (same as escalera)
  winnerType?: 'first-place' | 'elimination';
  eliminationPhase?: 'octavos' | 'cuartos' | 'semifinal' | 'final';
  eliminationConfig?: {
    singleDateSchedule?: {
      date?: string;
      startTime?: string;
      endTime?: string;
      courts?: number;
    };
    phaseSchedules?: Record<string, {
      date?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  restWeeks?: number[];
}

interface LeagueRoundRobinViewProps {
  league: League;
  leagueMatches?: {
    matches: any[];
    standings: any[];
  } | null;
  onContactOrganizer?: () => void;
  onRegisterClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
}

const LeagueRoundRobinView = ({ league, leagueMatches, onContactOrganizer, onRegisterClick, isUserRegistered, registrationLoading }: LeagueRoundRobinViewProps) => {
  const [selectedCalendarWeek, setSelectedCalendarWeek] = useState<any>(null);
  const [selectedEncuentro, setSelectedEncuentro] = useState<number | null>(null);
  const [currentPhase, setCurrentPhase] = useState<'regular' | 'elimination'>('regular');
  
  // Fetch user registration, prizes, and attendance
  const { registration } = useUserRegistration(league.id);
  const { prizes: dbPrizes } = usePrizes(league.id);
  const { uiAttendance, updateAttendance, isUpdating: isUpdatingAttendance } = useLeagueAttendance(league.id);
  
  // Fetch real participants for ranking table
  const { participants: realParticipants, isLoading: isLoadingParticipants } = useRoundRobinParticipants(league.id);
  
  // Fetch real matches from database
  const { 
    userMatchForEncounter, 
    userMatchesForEncounter,
    userTeam,
    opponentTeam,
    opponentAttendanceStatus, 
    isLoading: isLoadingMatches,
    setSelectedEncounter: setMatchSelectedEncounter,
    allScheduledMatches,
    allMatchesByEncounter
  } = useLeagueMatches(league.id, selectedEncuentro || 1);
  
  // Structure prizes for PrizesDialog - CORRECTED: Extract subcategory from prize.category format
  const allCategoryPrizes: CategoryPrizes[] = dbPrizes.reduce((acc: CategoryPrizes[], prize) => {
    // Extract subcategory from format "2a - parejas" or "3era"
    const subcategoryMatch = prize.category.match(/^(\d+[a-z]*)/i);
    const subcategoryFromPrize = subcategoryMatch ? subcategoryMatch[1] : prize.category;
    
    // Use registration category (e.g., "Masculino") or fallback to league subcategory
    const mainCategory = registration?.category || league.subcategory?.split(' ')[0] || 'General';
    
    const existingCategory = acc.find(p => 
      p.category === mainCategory && p.subcategory === subcategoryFromPrize
    );
    
    const prizeData = {
      type: prize.description.toLowerCase().includes('trofeo') ? 'trofeo' as const :
            prize.description.toLowerCase().includes('raqueta') || prize.description.toLowerCase().includes('pala') ? 'raqueta' as const :
            prize.description.toLowerCase().includes('$') || prize.description.toLowerCase().includes('€') || prize.amount ? 'dinero' as const :
            'certificado' as const,
      amount: prize.amount,
      description: prize.description
    };
    
    const isFirstPlace = prize.description.toLowerCase().includes('1er') || 
                         prize.description.toLowerCase().includes('primer');
    
    if (existingCategory) {
      if (isFirstPlace) {
        existingCategory.firstPlace.push(prizeData);
      } else {
        existingCategory.secondPlace = existingCategory.secondPlace || [];
        existingCategory.secondPlace.push(prizeData);
      }
    } else {
      acc.push({
        category: mainCategory,
        subcategory: subcategoryFromPrize,
        firstPlace: isFirstPlace ? [prizeData] : [],
        secondPlace: isFirstPlace ? [] : [prizeData]
      });
    }
    return acc;
  }, []);
  
  const userCategory = registration?.category && registration?.subcategory 
    ? `${registration.category} ${registration.subcategory}` 
    : undefined;

  // (Mock match results removed - using EncounterResultsSection with real DB data)

  // Fetch schedule dates from tournament_schedules (organizer-managed)
  const { gameDates, eliminationPhases, loading: scheduleLoading } = useLeagueSchedule(league.id);
  
  // Generate weeks dynamically - prioritize tournament_schedules data
  const timeRange = formatTimeRange(league.start_time, league.end_time);
  
  const encounterWeeks = useMemo(() => {
    // If we have gameDates from tournament_schedules, use those
    if (gameDates && gameDates.length > 0) {
      const scheduleWeeks = gameDates.map((gd, index) => {
        const status = calculateGameDateStatus(gd.date);
        const isTennis = league.sport.toLowerCase() === 'tennis' || league.sport.toLowerCase() === 'tenis';
        const fullDate = new Date(gd.date);
        return {
          number: index + 1,
          date: isTennis ? extractDateFromFormatted(gd.formattedDate) : gd.formattedDate,
          dateEnd: isTennis ? calculateWeekEndDate(fullDate) : undefined,
          fullDate,
          status,
          time: timeRange
        };
      });
      
      // If no current week, mark the first future as current
      const hasCurrentWeek = scheduleWeeks.some(w => w.status === 'current');
      if (!hasCurrentWeek) {
        const firstFutureIndex = scheduleWeeks.findIndex(w => w.status === 'future');
        if (firstFutureIndex !== -1) {
          scheduleWeeks[firstFutureIndex].status = 'current';
        }
      }
      
      return scheduleWeeks;
    }
    
    // Fallback to calculating from total_weeks
    return generateLeagueWeeks(
      league.date,
      league.total_weeks || 8,
      league.start_time,
      league.end_time,
      league.sport
    );
  }, [gameDates, league.date, league.total_weeks, league.start_time, league.end_time, timeRange]);

  // Interleave rest weeks
  const weeks = useMemo(() => {
    return interleaveRestWeeks(encounterWeeks, league.restWeeks || [], league.sport);
  }, [encounterWeeks, league.restWeeks, league.sport]);
  
  // Elimination phase support (replicating LeagueEscaleraView logic)
  const hasEliminationPhase = league.winnerType === 'elimination';
  
  const eliminationWeeks = useMemo(() => {
    if (!hasEliminationPhase) return [];

    const phaseOrder = ['octavos', 'cuartos', 'semifinal', 'final'];
    const phaseLabels: Record<string, string> = {
      'octavos': 'Octavos',
      'cuartos': 'Cuartos',
      'semifinal': 'Semifinales',
      'final': 'Final'
    };
    const phases: Array<{ number: number; date: string; status: 'past' | 'current' | 'future'; time: string; phase: string }> = [];

    // Priority 1: eliminationPhases from schedule_data (accounts for rest weeks)
    if (Object.keys(eliminationPhases).length > 0) {
      let weekNumber = weeks.length + 1;
      phaseOrder.forEach(phaseName => {
        const schedule = eliminationPhases[phaseName];
        if (schedule?.date) {
          const phaseDate = parseLocalDate(schedule.date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          phaseDate.setHours(0, 0, 0, 0);

          let status: 'past' | 'current' | 'future' = 'future';
          if (phaseDate < today) status = 'past';
          else if (phaseDate.getTime() === today.getTime()) status = 'current';

          phases.push({
            number: weekNumber++,
            date: phaseDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
            status,
            time: schedule.startTime && schedule.endTime
              ? `${schedule.startTime.slice(0, 5)} - ${schedule.endTime.slice(0, 5)}`
              : schedule.time ? `${schedule.time.slice(0, 5)}` : '18:00 - 20:00',
            phase: phaseLabels[phaseName] || phaseName
          });
        }
      });
      if (phases.length > 0) return phases;
    }

    // Priority 2: league.eliminationConfig (fallback, original dates)
    const config = league.eliminationConfig;
    if (!config) return [];

    const restWeekShiftDays = (league.restWeeks?.length || 0) * 7;

    if (config.phaseSchedules && Object.keys(config.phaseSchedules).length > 0) {
      let weekNumber = weeks.length + 1;
      phaseOrder.forEach(phaseName => {
        const schedule = config.phaseSchedules?.[phaseName];
        if (schedule && schedule.date) {
          const phaseDate = addDays(parseLocalDate(schedule.date), restWeekShiftDays);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          phaseDate.setHours(0, 0, 0, 0);

          let status: 'past' | 'current' | 'future' = 'future';
          if (phaseDate < today) status = 'past';
          else if (phaseDate.getTime() === today.getTime()) status = 'current';

          phases.push({
            number: weekNumber++,
            date: phaseDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
            status,
            time: schedule.startTime && schedule.endTime 
              ? `${schedule.startTime.slice(0, 5)} - ${schedule.endTime.slice(0, 5)}`
              : '18:00 - 20:00',
            phase: phaseLabels[phaseName] || phaseName
          });
        }
      });
    } else if (config.singleDateSchedule && config.singleDateSchedule.date) {
      const sched = config.singleDateSchedule;
      const phaseDate = addDays(parseLocalDate(sched.date!), restWeekShiftDays);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      phaseDate.setHours(0, 0, 0, 0);

      let status: 'past' | 'current' | 'future' = 'future';
      if (phaseDate < today) status = 'past';
      else if (phaseDate.getTime() === today.getTime()) status = 'current';

      phases.push({
        number: weeks.length + 1,
        date: phaseDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
        status,
        time: sched.startTime && sched.endTime 
          ? `${sched.startTime.slice(0, 5)} - ${sched.endTime.slice(0, 5)}`
          : '18:00 - 20:00',
        phase: 'Eliminación'
      });
    }

    return phases;
  }, [hasEliminationPhase, eliminationPhases, league.eliminationConfig, weeks.length, league.restWeeks]);

  const eliminationScheduleConfig: EliminationScheduleConfig | undefined = useMemo(() => {
    const config = league.eliminationConfig;
    const phaseLabels: Record<string, string> = {
      'semifinal': 'Semifinales',
      'final': 'Final',
      'cuartos': 'Cuartos',
      'octavos': 'Octavos'
    };

    // Priority 1: eliminationPhases from schedule_data
    if (Object.keys(eliminationPhases).length > 0) {
      const formattedPhases: Record<string, { date: string; time: string }> = {};
      Object.entries(eliminationPhases).forEach(([phase, schedule]) => {
        const phaseDate = parseLocalDate(schedule.date);
        formattedPhases[phaseLabels[phase] || phase] = {
          date: phaseDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
          time: formatTo12h(schedule.time || schedule.startTime || '18:00')
        };
      });
      
      if (Object.keys(formattedPhases).length > 0) {
        return {
          isSingleDate: false,
          phaseSchedules: formattedPhases
        };
      }
    }

    // Priority 2: league.eliminationConfig (fallback)
    const restWeekShiftDays = (league.restWeeks?.length || 0) * 7;
    
    if (config?.singleDateSchedule?.date) {
      const scheduleDate = addDays(parseLocalDate(config.singleDateSchedule.date), restWeekShiftDays);
      return {
        isSingleDate: true,
        singleDateInfo: {
          date: scheduleDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
          startTime: formatTo12h(config.singleDateSchedule.startTime || '18:00'),
          endTime: formatTo12h(config.singleDateSchedule.endTime || '21:00')
        }
      };
    }
    
    if (config?.phaseSchedules && Object.keys(config.phaseSchedules).length > 0) {
      const formattedPhases: Record<string, { date: string; time: string }> = {};
      Object.entries(config.phaseSchedules).forEach(([phase, schedule]) => {
        if (schedule.date) {
          const phaseDate = addDays(parseLocalDate(schedule.date), restWeekShiftDays);
          formattedPhases[phaseLabels[phase] || phase] = {
            date: phaseDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
            time: formatTo12h(schedule.startTime || '18:00')
          };
        }
      });
      
      if (Object.keys(formattedPhases).length > 0) {
        return {
          isSingleDate: false,
          phaseSchedules: formattedPhases
        };
      }
    }
    
    return undefined;
  }, [league.eliminationConfig, eliminationPhases, league.restWeeks]);
  
  const weeksRemaining = calculateWeeksRemaining(weeks);
  
  // (Mock future matches removed - using EncounterResultsSection with real DB data)

  // Calculate total matches from database fields
  const totalMatches = (league.total_weeks || 8) * (league.matches_per_week || 1);
  const weeksPlayed = weeks.filter(w => w.status === 'past').length;
  const matchesPlayed = weeksPlayed * (league.matches_per_week || 1);
  
  const leagueData = {
    leagueName: league.name,
    startDate: league.date,
    endDate: league.endDate || "17 May 2024",
    totalMatches,
    matchesPlayed,
    sport: league.sport,
    modalidad: league.gameType,
    partner: registration?.partner_name || league.partner || '',
    club: league.club,
    category: registration?.category || league.category || '',
    subcategory: registration?.subcategory || league.subcategory || '',
    nextMatchDate: league.status === 'upcoming' ? "Por confirmar" : "Sábado 12 Abr",
    nextMatchTime: league.status === 'upcoming' ? "Por confirmar" : formatTimeRange(league.start_time, league.end_time),
    nextMatchCourt: league.status === 'upcoming' ? "Por confirmar" : "Central 1",
    opponent: league.status === 'upcoming' ? "Por confirmar" : "Carlos Rivera / Laura Sánchez",
    leagueType: 'round-robin' as const
  };

  // Use real participants from database, fallback to mock for UI preview
  const rankingPlayers = realParticipants.length > 0 ? realParticipants : [
    { position: 1, name: "TBD", partner: "TBD", wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 2, name: "TBD", partner: "TBD", wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 3, name: "TBD", partner: "TBD", wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 4, name: "TBD", partner: "TBD", wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
  ];

  const handleWeekClick = (week: any) => {
    if (week.isRest) return;
    setSelectedCalendarWeek(week);
  };

  const handleAttendanceChange = (weekNumber: number, attendance: 'attending' | 'not-attending') => {
    // Save to database via hook
    updateAttendance(weekNumber, attendance);
  };

  // Handle encounter selection change from the match card
  const handleEncuentroChange = (weekNumber: number) => {
    setSelectedEncuentro(weekNumber);
    setMatchSelectedEncounter(weekNumber);
  };

  // Determine current/selected week for match data
  const currentWeekForMatch = selectedEncuentro 
    ? weeks.find(w => w.number === selectedEncuentro) 
    : (selectedCalendarWeek || weeks.find(w => w.status === 'current') || weeks[0]);

  // Set initial encounter to current week
  useEffect(() => {
    if (!selectedEncuentro) {
      const currentWeek = weeks.find(w => w.status === 'current');
      if (currentWeek) {
        setSelectedEncuentro(currentWeek.number);
        setMatchSelectedEncounter(currentWeek.number);
      }
    }
  }, [weeks, selectedEncuentro, setMatchSelectedEncounter]);

  // Helper to get user attendance status for current encounter
  const getUserAttendanceForEncounter = (): 'confirmed' | 'not_attending' | 'pending' => {
    const encounterNumber = selectedEncuentro || 1;
    const status = uiAttendance[encounterNumber];
    if (status === 'attending') return 'confirmed';
    if (status === 'not-attending') return 'not_attending';
    return 'pending';
  };

  // Calculate available encounters from weeks
  const availableEncounters = useMemo(() => {
    return weeks.map(w => w.number);
  }, [weeks]);

  // Calculate encounter status for the selected encounter
  const encounterStatus = useMemo((): 'waiting_previous' | 'not_scheduled' | 'completed' | 'in_progress' | 'scheduled' => {
    const currentWeek = weeks.find(w => w.number === (selectedEncuentro || 1));
    if (!currentWeek) return 'not_scheduled';
    
    if (currentWeek.status === 'past') return 'completed';
    if (currentWeek.status === 'current') return 'in_progress';
    if (currentWeek.status === 'future') return 'scheduled';
    return 'not_scheduled';
  }, [weeks, selectedEncuentro]);

  return (
    <div className="space-y-8">
      {/* League Information */}
      <LeagueInfo 
        leagueName={leagueData.leagueName}
        startDate={leagueData.startDate}
        endDate={leagueData.endDate}
        timeRange={formatTimeRange(league.start_time, league.end_time)}
        totalMatches={leagueData.totalMatches}
        matchesPlayed={leagueData.matchesPlayed}
        sport={leagueData.sport}
        modalidad={leagueData.modalidad}
        partner={leagueData.partner}
        club={leagueData.club}
        category={leagueData.category}
        subcategory={leagueData.subcategory}
        leagueType={leagueData.leagueType}
        allCategoryPrizes={allCategoryPrizes}
        userCategory={userCategory}
        gameRules={[
          { label: "Formato de Juego", value: "Mejor de 2 de 3 sets" },
          { label: "Desempate de Set", value: "Tie-break a 7 puntos en 6-6" },
          { label: "Punto de Oro", value: "Sí en 40-40" },
          { label: "Tercer Set", value: "Súper Tie-break a 10 puntos" }
        ]}
        onContactOrganizer={onContactOrganizer}
        groupRules={league.group_rules}
        additionalRules={league.additional_rules}
        gameDays={league.game_days}
        startTime={league.start_time}
        endTime={league.end_time}
        organizerName={league.organizer}
      />

      {/* Weekly Calendar with Phase Toggle */}
      <WeeklyCalendar 
        weeks={weeks} 
        weeksRemaining={league.status === 'upcoming' ? (league.total_weeks || 8) : weeksRemaining} 
        onWeekClick={handleWeekClick}
        onAttendanceChange={handleAttendanceChange}
        initialAttendance={uiAttendance}
        isUpdatingAttendance={isUpdatingAttendance}
        showPhaseToggle={hasEliminationPhase}
        currentPhase={currentPhase}
        onPhaseChange={setCurrentPhase}
        eliminationWeeks={eliminationWeeks}
        eliminationPhase={league.eliminationPhase}
        eliminationScheduleConfig={eliminationScheduleConfig}
        sport={league.sport}
      />

      {/* Registration Button - Solo si NO está inscrito */}
      {!isUserRegistered && onRegisterClick && (
        <div className="px-0">
          <Button
            onClick={REGISTRATION_ENABLED ? onRegisterClick : undefined}
            disabled={registrationLoading || !REGISTRATION_ENABLED}
            className={`w-full h-16 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
              !REGISTRATION_ENABLED
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {registrationLoading ? 'Cargando...' : '¡Inscríbeme!'}
          </Button>
        </div>
      )}

      {/* Encounter Results with Elimination Phases */}
      <EncounterResultsSection
        selectedEncounter={selectedEncuentro || 1}
        onEncounterChange={handleEncuentroChange}
        availableEncounters={availableEncounters}
        encounterStatus={encounterStatus}
        userMatchForEncounter={userMatchForEncounter}
        userMatchesForEncounter={userMatchesForEncounter}
        userTeam={userTeam}
        opponentTeam={opponentTeam}
        weeks={weeks.map(w => ({ number: w.number, date: w.date, status: w.status as 'past' | 'current' | 'future' }))}
        league={league}
        userAttendanceStatus={getUserAttendanceForEncounter()}
        opponentAttendanceStatus={opponentAttendanceStatus}
        isLoadingMatches={isLoadingMatches}
        onWeekChange={handleEncuentroChange}
        eliminationPhase={league.eliminationPhase}
        hasEliminationPhase={hasEliminationPhase}
        allScheduledMatches={allScheduledMatches}
      />

      {/* Ranking Table */}
      <LeagueRankingTable 
        players={rankingPlayers} 
        modalidad={leagueData.modalidad} 
        type="round-robin"
        sport={leagueData.sport}
      />
    </div>
  );
};

export default LeagueRoundRobinView;